import { useState, useEffect } from 'react';
import Layout from '@/components/layout/Layout';
import PricingSection from '@/components/home/PricingSection';
import { Check, X, BookOpen, Video, Users, Clock, Shield, MessageSquare } from 'lucide-react';
import { supabase } from '@/lib/supabaseClient';
import { safeFetch } from '@/lib/safeFetch';

interface CurriculumItem {
  module: string;
  title: string;
  topics: string[];
}

const courseFeatures = [
  {
    icon: Video,
    title: '50+ Video Lessons',
    description: 'Comprehensive video content covering all aspects of trading',
  },
  {
    icon: BookOpen,
    title: 'Study Materials',
    description: 'Downloadable PDFs and reference materials',
  },
  {
    icon: Users,
    title: 'Community Access',
    description: 'Join Telegram and WhatsApp groups for peer support',
  },
  {
    icon: MessageSquare,
    title: 'Live Q&A Sessions',
    description: 'Weekly interactive sessions to clear doubts and analyze market trends',
  },
  {
    icon: Clock,
    title: 'Limited Time Access',
    description: 'Access course updates and new content for a limited duration',
  },
];

const Courses = () => {
  const [curriculum, setCurriculum] = useState<CurriculumItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDynamicCurriculum = async () => {
      try {
        console.log("--- LMS TELEMETRY: FETCHING CURRICULUM ---");
        // Find most recent active course
        const { data: courseData } = await supabase
          .from('courses')
          .select('id')
          .eq('status', 'active')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (courseData) {
          const [modulesData, lessonsData] = await Promise.all([
            safeFetch(supabase.from('course_modules').select('*').eq('course_id', courseData.id).order('order_index', { ascending: true }), []),
            safeFetch(supabase.from('lessons').select('module_id, title').eq('course_id', courseData.id).order('order_index', { ascending: true }), [])
          ]);

          if (modulesData.length > 0) {
            const dynamicCurriculum: CurriculumItem[] = (modulesData as any[]).map((m) => ({
              module: `Module ${m.order_index}`,
              title: m.title,
              topics: (lessonsData as any[]).filter((l) => l.module_id === m.id).map((l) => l.title)
            }));
            setCurriculum(dynamicCurriculum);
          }
        }
      } catch (err) {
        console.error('Failed to load dynamic curriculum', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDynamicCurriculum();
  }, []);
  return (
    <Layout>
      {/* Hero */}
      <section className="relative overflow-hidden bg-background pt-8 pb-12">
        <div className="absolute inset-0 z-0">
          <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-accent/5 blur-[120px]" />
        </div>

        <div className="container-max text-center relative z-10">
          <span className="inline-block px-4 py-2 bg-accent/5 border border-accent/20 rounded-full text-accent-foreground text-[10px] font-black uppercase tracking-[0.2em] mb-8">
            Available Courses
          </span>
          <h1 className="text-2xl md:text-4xl font-black text-primary mb-6 tracking-tighter uppercase">
            Professional <br />
            <span className="text-gradient-gold">Trading Training</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto font-medium leading-relaxed">
            Simple and effective trading lessons. Built for those who want clear, easy to follow guidance and consistent results.
          </p>
        </div>
      </section>

      {/* Course Features */}
      <section className="section-padding bg-background relative overflow-hidden">
        <div className="container-max relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <span className="text-[10px] font-black uppercase tracking-[0.3em] text-accent mb-4 block">Quality Learning</span>
            <h2 className="heading-lg text-primary">Everything You <span className="text-gradient-gold">Need</span></h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
            {courseFeatures.map((feature, index) => (
              <div key={index} className="flex items-start gap-6 p-8 rounded-3xl bg-white/40 border border-white/60 hover:bg-white hover:shadow-premium transition-all duration-500">
                <div className="w-14 h-14 rounded-2xl bg-primary/5 flex items-center justify-center flex-shrink-0 border border-primary/5">
                  <feature.icon className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-black text-primary uppercase tracking-tighter mb-2">{feature.title}</h3>
                  <p className="text-sm font-medium text-muted-foreground leading-relaxed">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Curriculum */}
      <section className="section-padding bg-secondary/10 relative overflow-hidden">
        <div className="container-max relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <span className="inline-block px-4 py-2 bg-primary/5 border border-primary/10 rounded-full text-primary text-[10px] font-black uppercase tracking-[0.2em] mb-6">
              Course Content
            </span>
            <h2 className="heading-lg text-primary mb-6">
              What You Will <span className="text-gradient-gold">Learn</span>
            </h2>
            <p className="text-lg text-muted-foreground font-medium">
              A comprehensive set of modules designed to give you clarity and confidence in trading.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {curriculum.length === 0 ? (
              <div className="col-span-full text-center py-20 border-2 border-dashed border-primary/10 rounded-[2.5rem] bg-white/40">
                <BookOpen className="w-12 h-12 text-primary/20 mx-auto mb-6" />
                <h3 className="text-xl font-black text-primary uppercase tracking-tighter mb-2">Curriculum Under Maintenance</h3>
                <p className="text-muted-foreground font-medium max-w-sm mx-auto">No study nodes have been synchronized with the database yet. Please check back shortly.</p>
              </div>
            ) : curriculum.map((item, index) => (
              <div key={index} className="card-premium group !p-10 bg-white/60 border-white/60 hover:bg-white flex flex-col items-start justify-start h-full">
                <div className="flex items-center justify-between mb-8 w-full">
                  <span className="text-[10px] font-black uppercase tracking-[0.3em] text-accent block">
                    {item.module}
                  </span>
                  <div className="w-8 h-8 rounded-lg bg-primary/5 flex items-center justify-center">
                    <BookOpen className="w-4 h-4 text-primary/40" />
                  </div>
                </div>
                <h3 className="text-xl font-black text-primary mb-6 uppercase tracking-tighter group-hover:text-accent transition-colors duration-300 w-full">{item.title}</h3>
                <ul className="space-y-4 w-full">
                  {item.topics.map((topic, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-3 h-3 text-accent" />
                      </div>
                      <span className="text-sm font-semibold text-primary/70">{topic}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <PricingSection />

      {/* Suitability Section */}
      <section className="section-padding bg-background relative overflow-hidden">
        <div className="container-max relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-4 py-2 bg-accent/5 border border-accent/20 rounded-full text-accent text-[10px] font-black uppercase tracking-[0.2em] mb-6">
              Who is this for?
            </span>
            <h2 className="heading-lg text-primary mb-6">
              Is This Course <span className="text-gradient-gold">For You?</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            {/* For You */}
            <div className="card-premium !p-8 md:!p-10 bg-white/60 border-white/60 flex flex-col h-full hover:border-[#22C55E]/30 transition-all duration-500">
              <div className="flex items-center gap-4 mb-8">
                <div className="w-12 h-12 rounded-2xl bg-[#22C55E]/10 flex items-center justify-center border border-[#22C55E]/20">
                  <Check className="w-6 h-6 text-[#22C55E]" />
                </div>
                <h3 className="text-xl font-black text-primary uppercase tracking-tighter">
                  This Course is <span className="text-[#22C55E]">For You</span> If:
                </h3>
              </div>
              <ul className="space-y-5">
                {[
                  "You're new to stock market and want structured learning",
                  "You want to understand technical chart analysis",
                  "You prefer self-paced video learning",
                  "You want to learn risk management fundamentals",
                  "You're looking for educational content, not tips"
                ].map((text, i) => (
                  <li key={i} className="flex items-start gap-4 group">
                    <div className="w-6 h-6 rounded-full bg-[#22C55E]/10 flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:scale-110 transition-transform">
                      <Check className="w-3.5 h-3.5 text-[#22C55E]" />
                    </div>
                    <span className="text-base font-semibold text-primary/80 leading-snug">{text}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Not For You */}
            <div className="card-premium !p-8 md:!p-10 bg-white/60 border-white/60 flex flex-col h-full hover:border-[#EF4444]/30 transition-all duration-500">
              <div className="flex items-center gap-4 mb-8">
                <div className="w-12 h-12 rounded-2xl bg-[#EF4444]/10 flex items-center justify-center border border-[#EF4444]/20">
                  <X className="w-6 h-6 text-[#EF4444]" />
                </div>
                <h3 className="text-xl font-black text-primary uppercase tracking-tighter">
                  This Course is <span className="text-[#EF4444]">NOT For You</span> If:
                </h3>
              </div>
              <ul className="space-y-5">
                {[
                  "You're looking for guaranteed profit tips",
                  "You expect buy/sell recommendations",
                  "You want someone to manage your money",
                  "You're looking for get-rich-quick schemes",
                  "You're not willing to practice and learn"
                ].map((text, i) => (
                  <li key={i} className="flex items-start gap-4 group">
                    <div className="w-6 h-6 rounded-full bg-[#EF4444]/10 flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:scale-110 transition-transform">
                      <X className="w-3.5 h-3.5 text-[#EF4444]" />
                    </div>
                    <span className="text-base font-semibold text-primary/80 leading-snug">{text}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Courses;
